import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

/**
 * Where a page shows its save status, for forms that sit below it.
 *
 * A page with autosaving sections (the Visualizer settings, and later others)
 * provides one of these and shows `AutosaveIndicator` in its top bar; each
 * section's `useAutosave` reports into it without knowing where it is shown.
 */
export interface AutosaveReporter {
    report: (status: AutosaveStatus, retry: () => void) => void;
}

export const AutosaveReporterContext = createContext<AutosaveReporter | null>(null);

/** Status plus a retry, for a page that shows one indicator for all of its sections. */
export function useAutosaveReporterState() {
    const [status, setStatus] = useState<AutosaveStatus>('idle');
    const retryRef = useRef<() => void>(() => {});
    const reporter = useRef<AutosaveReporter>({
        report: (next, retry) => {
            retryRef.current = retry;
            setStatus(next);
        },
    }).current;
    const retry = useCallback(() => retryRef.current(), []);
    return { status, retry, reporter };
}

interface UseAutosaveOptions<T> {
    /** What would be saved. Compared by JSON, so it should hold only what the save sends. */
    data: T;
    /**
     * Whether the stored values have loaded. Nothing saves before this: a form
     * that has half-loaded would otherwise write its defaults over real data.
     * The first `data` seen once ready is the baseline.
     */
    ready: boolean;
    /** Stores the current data. Throw to report a failure. */
    save: () => Promise<unknown>;
    /** Quiet time after the last change before saving. */
    delay?: number;
}

/**
 * Save a form by itself, a moment after the last change.
 *
 * There is no Save button to forget: edits are stored `delay` ms after they
 * stop, and whatever is still waiting is stored when the form goes away (a
 * section switch, a navigation) or the tab closes. One save runs at a time;
 * edits made during it are picked up by the next.
 *
 * The status is returned, and also reported to an enclosing
 * `AutosaveReporterContext` if there is one, which is how a page shows a single
 * indicator in its top bar.
 */
export function useAutosave<T>({ data, ready, save, delay = 1500 }: UseAutosaveOptions<T>) {
    const reporter = useContext(AutosaveReporterContext);
    const [status, setStatus] = useState<AutosaveStatus>('idle');

    const json = ready ? JSON.stringify(data) : null;
    const latest = useRef({ json, save, reporter });
    latest.current = { json, save, reporter };

    const baselineRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);
    const mountedRef = useRef(true);

    const flushRef = useRef<() => Promise<void>>(async () => {});

    const report = useCallback((next: AutosaveStatus) => {
        if (mountedRef.current) setStatus(next);
        latest.current.reporter?.report(next, () => { void flushRef.current(); });
    }, []);

    const flush = useCallback(async () => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const sent = latest.current.json;
        if (baselineRef.current === null || sent === null || sent === baselineRef.current) return;
        // One at a time; whatever changed meanwhile goes in the follow-up below.
        if (inFlightRef.current) return;

        inFlightRef.current = true;
        report('saving');
        try {
            await latest.current.save();
            baselineRef.current = sent;
        } catch (error) {
            console.error('Autosave failed:', error);
            report('error');
            return;
        } finally {
            inFlightRef.current = false;
        }

        if (latest.current.json !== baselineRef.current) {
            void flushRef.current();
        } else {
            report('saved');
        }
    }, [report]);
    flushRef.current = flush;

    // The first data seen once loaded is what is already stored.
    useEffect(() => {
        if (ready && baselineRef.current === null) baselineRef.current = json;
    }, [ready, json]);

    // A change starts (or restarts) the wait.
    useEffect(() => {
        if (!ready || baselineRef.current === null || json === baselineRef.current) return;
        report('pending');
        if (timerRef.current !== null) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            void flushRef.current();
        }, delay);
    }, [json, ready, delay, report]);

    // Leaving must not drop the last few seconds of edits.
    useEffect(() => {
        mountedRef.current = true;
        const hasUnsaved = () =>
            inFlightRef.current
            || (baselineRef.current !== null && latest.current.json !== null && latest.current.json !== baselineRef.current);
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasUnsaved()) return;
            void flushRef.current();
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            mountedRef.current = false;
            void flushRef.current();
        };
    }, []);

    return { status, flush };
}
